import React, { createContext, useContext, useState, useCallback } from 'react';

const STORAGE_KEYS = {
  ID_TOKEN: 'impersonation_id_token',
  ACCESS_TOKEN: 'impersonation_access_token',
  EXP: 'impersonation_tokens_exp',
  SUBJECT: 'impersonation_subject',
} as const;

export function getImpersonationIdToken(): string | null {
  return sessionStorage.getItem(STORAGE_KEYS.ID_TOKEN);
}

export function clearImpersonationSession(): void {
  Object.values(STORAGE_KEYS).forEach((k) => sessionStorage.removeItem(k));
}

interface ImpersonationContextType {
  isImpersonating: boolean;
  subject: string | null;
  save: (idToken: string, accessToken: string, exp: number, subject: string) => void;
  clear: () => void;
}

const ImpersonationContext = createContext<ImpersonationContextType | undefined>(undefined);

export const ImpersonationProvider = ({ children }: { children: React.ReactNode }) => {
  const [subject, setSubject] = useState<string | null>(
    () => sessionStorage.getItem(STORAGE_KEYS.SUBJECT),
  );
  const [idToken, setIdToken] = useState<string | null>(
    () => sessionStorage.getItem(STORAGE_KEYS.ID_TOKEN),
  );

  const save = useCallback(
    (idToken: string, accessToken: string, exp: number, subject: string) => {
      sessionStorage.setItem(STORAGE_KEYS.ID_TOKEN, idToken);
      sessionStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
      sessionStorage.setItem(STORAGE_KEYS.EXP, String(exp));
      sessionStorage.setItem(STORAGE_KEYS.SUBJECT, subject);
      setIdToken(idToken);
      setSubject(subject);
    },
    [],
  );

  const clear = useCallback(() => {
    clearImpersonationSession();
    setIdToken(null);
    setSubject(null);
    window.location.replace('/');
  }, []);

  return (
    <ImpersonationContext.Provider
      value={{ isImpersonating: !!idToken, subject, save, clear }}
    >
      {children}
    </ImpersonationContext.Provider>
  );
};

export const useImpersonation = () => {
  const ctx = useContext(ImpersonationContext);
  if (!ctx) throw new Error('useImpersonation must be used within ImpersonationProvider');
  return ctx;
};
